/**
 * The journey's two time zone settings, as the Journey editor's Time & availability panel edits them
 * (CC 0517, JAI-3213). They are the SAME stored fields the All Journeys designer edits:
 * `steps.datetime.showTimezone` and `steps.datetime.allowCustomerTimezone`.
 *
 * This editor's other changes stay on screen only (JourneysHome: "Page settings save from All
 * Journeys"). These two are written at once through `patchAdminJourney` (one leaf per change), so the
 * switch never shows a value the journey does not hold. The panel shows the new value only after
 * the write resolves.
 */
import { patchAdminJourney } from "./journeys-api";
import type { Journey } from "./journey-model";

export type TimezoneField = "showTimezone" | "allowCustomerTimezone";

/** The stored values, with the contract's defaults (shown on, choosing off) for a journey that has none. */
export function timezoneSettingsOf(j: Journey | null | undefined): { showTimezone: boolean; allowCustomerTimezone: boolean } {
  const dt = (j && j.steps && (j.steps as Record<string, unknown>).datetime) as Record<string, unknown> | undefined;
  return {
    showTimezone: dt && typeof dt.showTimezone === "boolean" ? dt.showTimezone : true,
    allowCustomerTimezone: dt && typeof dt.allowCustomerTimezone === "boolean" ? dt.allowCustomerTimezone : false,
  };
}

/** Write one setting, then answer the journey as stored. Throws the server's refusal. */
export async function saveTimezoneSetting(
  j: Journey,
  field: TimezoneField,
  value: boolean,
  patch: (id: string, body: Record<string, unknown>) => Promise<unknown> = patchAdminJourney,
): Promise<Journey> {
  if (!j || !j.id) throw new Error("This journey has not been saved yet.");
  await patch(String(j.id), { steps: { datetime: { [field]: value } } });
  const steps = (j.steps || {}) as Record<string, unknown>;
  const dt = (steps.datetime || {}) as Record<string, unknown>;
  return { ...j, steps: { ...steps, datetime: { ...dt, [field]: value } } };
}

/**
 * The Journey editor's switches over ONE journey: each flip saves from the journey as the previous flip left it,
 * so a second flip never reads the first one's value from the stale prop. `current()` is what the panel shows.
 */
export function timezoneSettingsEditor(
  initial: Journey,
  patch: (id: string, body: Record<string, unknown>) => Promise<unknown> = patchAdminJourney,
): { current: () => { showTimezone: boolean; allowCustomerTimezone: boolean }; flip: (field: TimezoneField, value: boolean) => Promise<{ showTimezone: boolean; allowCustomerTimezone: boolean }> } {
  let latest = initial;
  return {
    current: () => timezoneSettingsOf(latest),
    flip: async (field, value) => {
      latest = await saveTimezoneSetting(latest, field, value, patch);
      return timezoneSettingsOf(latest);
    },
  };
}
